import { useCallback, useEffect, useState } from 'react';
import {
    isPlainObject, isString, cloneDeep, isNil,
} from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import ServiceManager from 'services/ServiceManager';

const DATE_KEYS = ['divorceDate', 'weddingDate', 'employmentContinuationDate', 'dateOfBirth'];

const modifyRequestPayload = (data: Record<string, any>): void => Object.keys(data).forEach(key => {
    if (isPlainObject(data[key])) {
        modifyRequestPayload(data[key]);
    }
    if (DATE_KEYS.includes(key) && data[key] === '') {
        data[key] = null;

        return;
    }
    if (isNil(data[key])) {
        delete data[key];
    }

    if (isString(data[key])) {
        data[key] = data[key].trim();
    }
});

const createRequestPayload = (data: any) => {
    const detailsCopy = cloneDeep(data.details);

    modifyRequestPayload(detailsCopy);

    return { ...data, details: detailsCopy };
};

interface UseContactOptions {
    contactId?: string | number;
}

export const useContact = (options?: UseContactOptions) => {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const {
        data: rawData,
        error,
        isLoading,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getContact',
        payload: options?.contactId,
        enabled: !!options?.contactId && isMounted,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60,
    });

    const queryClient = useQueryClient();

    const {
        data: rawUpdateData,
        error: errorUpdateContact,
        isPending: isLoadingUpdateContact,
        mutateAsync,
    } = useGenericAPIMutation();

    const getContact = useCallback(async ({ contactId = options?.contactId }: { contactId?: any } = {}) => {
        if (contactId) {
            const result:any = await queryClient.fetchQuery({
                queryKey: ['getData_getContact', contactId],
                queryFn: () => ServiceManager.clientService('getContact', [contactId]),
                staleTime: 0,
                gcTime: 0,
            });

            return result.data ?? null;
        }

        return null;
    }, [queryClient]);

    const updateContact = useCallback(async ({
        contactId = options?.contactId, params,
    }: { contactId?: any; params?: any } = {}) => {
        const response = await mutateAsync({
            service: 'customClientService',
            API: 'saveClientDetails',
            payload: { contactId, data: createRequestPayload(params) },
            invalidateQueryKeys: ['getData_getContact', contactId],
        });

        return response?.data ?? null;
    }, [mutateAsync]);

    return {
        data: rawData?.data ?? null,
        error,
        isLoading,
        getContact,
        refetchContact: () => queryClient.invalidateQueries({ queryKey: ['getData_getContact', options?.contactId] }),
        dataUpdateContact: rawUpdateData?.data ?? null,
        errorUpdateContact,
        isLoadingUpdateContact,
        updateContact,
    };
};
